import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Copy, Check } from 'lucide-react'
import { useTextValidation } from '@/hooks/useTextValidation'
import type { CompiledFieldRules } from '@/hooks/useTextValidation'

interface EditableDescriptionProps {
  value: string
  original: string
  onChange: (value: string) => void
  rules: CompiledFieldRules | undefined
}

export function EditableDescription({ value, original, onChange, rules }: EditableDescriptionProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [copied, setCopied] = useState(false)
  const { violations, severity, charsUsed, maxChars } = useTextValidation(value, rules)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const borderColor = severity === 'error'
    ? 'border-red-400 dark:border-red-600'
    : severity === 'warning'
      ? 'border-yellow-400 dark:border-yellow-600'
      : 'border-neutral-200 dark:border-neutral-700'

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {t('remake.field.description')}
        </label>
        <div className="flex items-center gap-1.5">
          {maxChars > 0 && (
            <span className={`text-xs ${charsUsed > maxChars ? 'text-red-500' : 'text-neutral-400'}`}>
              {t('remake.charsUsed').replace('{{used}}', String(charsUsed)).replace('{{max}}', String(maxChars))}
            </span>
          )}
          <button onClick={handleCopy} className="p-1 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300" title={t('remake.copied')}>
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setEditing(!editing)} className="p-1 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {editing ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          autoFocus
          rows={6}
          className={`w-full p-2 text-sm rounded-lg border ${borderColor} bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white resize-y focus:outline-none focus:ring-1 focus:ring-orange-400`}
        />
      ) : (
        <div className={`text-sm p-2 rounded-lg border ${borderColor} bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-white cursor-pointer whitespace-pre-wrap`} onClick={() => setEditing(true)}>
          {value || original}
        </div>
      )}
      {violations.map((v, i) => (
        <p key={i} className={`text-xs ${v.severity === 'error' ? 'text-red-500' : 'text-yellow-600 dark:text-yellow-400'}`}>{v.message}</p>
      ))}
    </div>
  )
}
